import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Pagination, Icon, Popover } from '@amzn/awsui-components-core';
import { Avatar, AvatarSize } from '@amzn/stencil-react-components/avatar';
import { AttendeePriority } from '../../interfaces/interface';
import { MEETING_CONSTANTS } from '../../constants/EAHuddleConstants';
import { EmployeePopoverCard } from '../attendees/EmployeePopoverCard';
import { GroupMembersPopoverContent } from '../attendees/GroupMembersPopoverContent';
import { BrassService, BrassGroupType } from 'src/services/BrassService';
import styles from './Sidebar.module.scss';

// ── GroupFavoriteWithPopover ───────────────────────────────────────────────────

const GroupFavoriteWithPopover: React.FC<{
  groupId: string;
  existingLoginNames: Set<string>;
  onAdd: (loginName: string, priority?: AttendeePriority) => void;
}> = ({ groupId, existingLoginNames, onAdd }) => {
  const [members, setMembers] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);

  const handleOpen = async () => {
    if (members || loading) return;
    setLoading(true);
    try {
      const result = await BrassService.listActorGroupMembership(groupId, BrassGroupType.AntGroup);
      setMembers(result);
    } catch {
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover
      triggerType="custom"
      position="bottom"
      size="large"
      dismissButton={true}
      renderWithPortal
      content={
        loading || members === null ? (
          <p style={{ fontSize: '10.8px', color: 'var(--huddle-gray-450)', margin: 0 }}>Loading members...</p>
        ) : members.length > 0 ? (
          <GroupMembersPopoverContent members={members} existingLogins={existingLoginNames} onAdd={(login, priority) => onAdd(login, priority)} />
        ) : (
          <p style={{ fontSize: '10.8px', color: 'var(--huddle-gray-450)', margin: 0 }}>No members found</p>
        )
      }
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={handleOpen}>
        <div className={styles.groupIconSm}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 3h12v1H2V3zm0 3h12v1H2V6zm0 3h8v1H2V9zm0 3h8v1H2v-1z" fill="currentColor" />
          </svg>
        </div>
        <span className={styles.groupLabelLink}>{groupId}</span>
      </span>
    </Popover>
  );
};

// ── FavoritesSection ──────────────────────────────────────────────────────────

const FavoritesPlusIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export interface FavoritesSectionProps {
  favoriteLoginNames: Set<string>;
  existingLoginNames: Set<string>;
  onFavoriteClick: (loginName: string, priority?: AttendeePriority) => void;
  isReadOnly: boolean;
  isMeetingRegistered: boolean;
}

export const FavoritesSection: React.FC<FavoritesSectionProps> = ({
  favoriteLoginNames,
  existingLoginNames,
  onFavoriteClick,
  isReadOnly,
  isMeetingRegistered
}) => {
  const favorites = Array.from(favoriteLoginNames);
  const [favoritesPage, setFavoritesPage] = useState(1);
  const favoritesPageSize = MEETING_CONSTANTS.FAVORITES_PAGE_SIZE;
  const favoritesTotalPages = Math.ceil(favorites.length / favoritesPageSize);
  const clampedPage = favoritesTotalPages > 0 ? Math.min(favoritesPage, favoritesTotalPages) : 1;
  const paginatedFavorites = favorites.slice((clampedPage - 1) * favoritesPageSize, clampedPage * favoritesPageSize);

  // Sync state so the Pagination control reflects the corrected page
  useEffect(() => {
    if (favoritesPage > favoritesTotalPages && favoritesTotalPages > 0) {
      setFavoritesPage(favoritesTotalPages);
    }
  }, [favoritesPage, favoritesTotalPages]);

  const pendingAdds = useRef(new Set<string>());

  const handleFavoriteAdd = useCallback(
    (loginName: string, priority?: AttendeePriority) => {
      if (pendingAdds.current.has(loginName)) return;
      pendingAdds.current.add(loginName);
      onFavoriteClick(loginName, priority);
      setTimeout(() => pendingAdds.current.delete(loginName), 5000);
    },
    [onFavoriteClick]
  );

  return (
    <div className={styles.favoritesSection}>
      <div className={styles.favoritesSectionContent}>
        <div className={styles.favoritesSectionHeader}>
          <Icon name="star-filled" size="small" />
          <p className={styles.favoritesSectionHeading}>Favorites ({favorites.length})</p>
        </div>

        {favorites.length === 0 ? (
          <div className={styles.favoritesEmptyState}>
            <Icon name="star" size="big" variant="subtle" />
            <p className={styles.favoritesEmptyText}>Click the star icon next to an attendee to add them here</p>
          </div>
        ) : (
          <div className={styles.favoritesList}>
            {paginatedFavorites.map((loginName) => {
              const isGroup = loginName.startsWith('group:');
              const displayName = isGroup ? loginName.slice(6) : loginName;
              const isAlreadyAdded = existingLoginNames.has(displayName);
              return (
                <div key={loginName} className={styles.favoriteItem}>
                  {isGroup ? (
                    <GroupFavoriteWithPopover groupId={displayName} existingLoginNames={existingLoginNames} onAdd={handleFavoriteAdd} />
                  ) : (
                    <EmployeePopoverCard loginName={displayName} renderWithPortal>
                      <Avatar username={displayName} size={AvatarSize.ExtraSmall} imageAltText={`Profile image of ${displayName}`} />
                    </EmployeePopoverCard>
                  )}
                  <div className={styles.favoriteActions}>
                    {isAlreadyAdded ? (
                      <span style={{ color: 'var(--huddle-success)', display: 'inline-flex' }} title="Already added">
                        <Icon name="status-positive" size="small" />
                      </span>
                    ) : !isReadOnly && isMeetingRegistered ? (
                      <button
                        type="button"
                        className={styles.favoriteIconButton}
                        aria-label={`Add ${displayName} to attendees`}
                        onClick={() => handleFavoriteAdd(loginName)}
                      >
                        <FavoritesPlusIcon />
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
            {favoritesTotalPages > 1 && (
              <div className={styles.paginationBar}>
                <Pagination
                  currentPageIndex={clampedPage}
                  pagesCount={favoritesTotalPages}
                  onChange={({ detail }) => setFavoritesPage(detail.currentPageIndex)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
